import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popconfirm,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  Stack,
} from "@altimateai/lego";
import {
  IconArchive,
  IconArchiveOff,
  IconClockHour9,
  IconDotsVertical,
  IconMessage,
  IconTrash,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { PlaygroundSession, SessionLifecycleListFilter } from "./types";
import { SessionStatus } from "./components/SessionStatus";

const formatRelativeTime = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  return `${diffInDays}d ago`;
};

interface PlaygroundSidebarRowProps {
  session: PlaygroundSession;
  isActive: boolean;
  listFilter: SessionLifecycleListFilter;
  onArchive: (sessionId: string) => void;
  onUnarchive: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
}

export const PlaygroundSidebarRow = ({
  session,
  isActive,
  listFilter,
  onArchive,
  onUnarchive,
  onDelete,
}: PlaygroundSidebarRowProps) => {
  const isArchivedView = listFilter === "archived";
  // Trim defensively: a whitespace-only ``session_intent`` is truthy
  // under ``||`` and would render as a visually blank row.
  const displayIntent = session.session_intent?.trim();

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={isActive}
        className={cn("al-items-start al-h-auto al-py-3 al-pl-3")}
        asChild
      >
        <Link to={`/studio/${session.session_id}`} aria-current={isActive ? "page" : undefined}>
          <div className="al-w-full al-min-w-0">
            <Stack className="al-mb-2 al-min-w-0">
              <IconMessage className="al-text-dark-gray al-w-4 al-h-4 al-mt-[3px] al-shrink-0" />
              <span
                className="al-font-medium al-text-left al-line-clamp-2 al-break-words al-text-dark-gray al-flex-1 al-min-w-0"
                title={displayIntent ? `${displayIntent}\n\n${session.name}` : session.name}
              >
                {displayIntent || session.name}
              </span>
            </Stack>

            <Stack
              direction="row"
              align="center"
              gap="xs"
              className="al-text-xs al-text-muted-foreground"
            >
              <IconClockHour9 className="al-w-4 al-h-4 al-text-light-gray" />
              <Stack gap="lg" align="center" justify="start">
                <span className="al-text-light-gray">{formatRelativeTime(session.updated_at)}</span>
                {session.status && <SessionStatus status={session.status} />}
              </Stack>
            </Stack>
          </div>
        </Link>
      </SidebarMenuButton>

      {/*
       * Kebab uses lego's SidebarMenuAction primitive: auto-positioned
       * absolute on the right, auto-adds `pr-8` to SidebarMenuButton via the
       * `data-sidebar=menu-action` peer selector so text never overlaps.
       * Always visible (not hover-gated) per review feedback — the icon is
       * subtle enough not to be visually noisy at rest.
       */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            aria-label={`Actions for ${session.name}`}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            className="al-text-dark-gray hover:al-bg-light-gray/40"
          >
            <IconDotsVertical />
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="al-w-40">
          {isArchivedView ? (
            <DropdownMenuItem onClick={() => onUnarchive(session.session_id)}>
              <IconArchiveOff className="al-w-4 al-h-4" /> Unarchive
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => onArchive(session.session_id)}>
              <IconArchive className="al-w-4 al-h-4" /> Archive
            </DropdownMenuItem>
          )}
          <Popconfirm
            title="Delete this conversation?"
            description="It will be hidden from your conversations. This cannot be undone."
            okText="Delete"
            cancelText="Cancel"
            onConfirm={() => onDelete(session.session_id)}
          >
            <DropdownMenuItem
              className="al-text-destructive focus:al-text-destructive"
              onSelect={(e: Event) => e.preventDefault()}
            >
              <IconTrash className="al-w-4 al-h-4" /> Delete
            </DropdownMenuItem>
          </Popconfirm>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};
